import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { SftpImportConfigurationWorkflow, ImportHierarchyOption, getImportHierarchyOptionDescriptions } from '../../../store/Models/Api/SftpImportModels';
import { RadioButtonGroup } from '../../core/RadioButtonGroup';
import { ToxnotComponent, ToxnotComponentProperties } from '../../core/BaseApplicationComponents/ToxnotComponent';

interface ConfigureLevelsStepLocalProps {
    goToNextStep: () => void;
    goToPrevStep: () => void;
    configData: SftpImportConfigurationWorkflow;
    onConfigChange: (data: Partial<SftpImportConfigurationWorkflow>) => void;
}

type ConfigureLevelsStepProps = ConfigureLevelsStepLocalProps & ToxnotComponentProperties;

class ConfigureLevelsStepComponent extends ToxnotComponent<ConfigureLevelsStepProps> {
    
    private handleHierarchyOptionChange = (newHierarchyOption: ImportHierarchyOption) => {
        const { configData, onConfigChange } = this.props;
        const updates: Partial<SftpImportConfigurationWorkflow> = { importHierarchyOptionType: newHierarchyOption };
        
        // Update existing column mappings if needed
        if (configData.spreadsheetHeadersToColumnIdMappings?.length > 0) {
            const isNewHierarchical = newHierarchyOption === ImportHierarchyOption.RepeatedMaterialIdentifiers;
            const wasHierarchical = configData.importHierarchyOptionType === ImportHierarchyOption.RepeatedMaterialIdentifiers;
            
            if (isNewHierarchical !== wasHierarchical) {
                updates.spreadsheetHeadersToColumnIdMappings = configData.spreadsheetHeadersToColumnIdMappings.map(mapping => ({
                    ...mapping,
                    level: isNewHierarchical ? (mapping.level ?? 1) : null
                }));
            }
        }
        
        onConfigChange(updates);
    };

    render() {
        const { configData, appServices } = this.props;
        const hierarchyOptionDescriptions = getImportHierarchyOptionDescriptions(appServices);

        return (
            <div>
                <h3>{this.localizeString("How should 3E Exchange determine the sub-materials hierarchy in your import?")}</h3>
                <div className="alert alert-info">
                    <p>
                        {this.localizeString("3E Exchange can import products/materials that contain a hierarchy of sub-materials. There are two ways to do this:")}
                    </p>
                    <p>
                        <b>{this.localizeString("Level")}:</b> {this.localizeString("Provide a Level column in your import that specifies the level in the hierarchy of each row.")} <a href="/files/Hierarchical BOM with Level Column.xlsx">{this.localizeString("Download an example")}</a>
                    </p>
                    <p>
                        <b>{this.localizeString("Repeated product/material names")}:</b> {this.localizeString("Provide product/material/sub-material names in each row.")} <a href="/files/Hierarchical BOM with Repeated Material Names.xlsx">{this.localizeString("Download an example")}</a>
                    </p>
                </div>
                <div className="form-group">
                    <label>{this.localizeString("Is there hierarchy represented in your import?")}</label>
                    <RadioButtonGroup<ImportHierarchyOption>
                        options={[
                            ImportHierarchyOption.LevelColumn,
                            ImportHierarchyOption.LevelColumnWithMultipleLevelsPerRow,
                            ImportHierarchyOption.RepeatedMaterialIdentifiers
                        ]}
                        value={configData.importHierarchyOptionType}
                        onValueChange={(value) => this.handleHierarchyOptionChange(value)}
                        getFriendlyNameForOptions={(option) => hierarchyOptionDescriptions[option]}
                        name="hierarchy-options"
                    />
                </div>
                <div className="text-right" style={{ marginTop: '15px' }}>
                    <button className="btn btn-default" onClick={this.props.goToPrevStep}>{this.localizeString("Back")}</button>
                    <button className="btn btn-toxnot-primary" style={{ marginLeft: "10px" }} onClick={this.props.goToNextStep}>{this.localizeString("Continue")}</button>
                </div>
            </div>
        );
    }
}

export const ConfigureLevelsStep = connect(
    (state: ApplicationState) => ({ appServices: state.appServices! })
)(ConfigureLevelsStepComponent);